import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatDate } from "../../lib/date";
import { toast } from "sonner";
import {
  TrendingUp, RefreshCw, Plus, Search, Loader2, Phone, Mail, Calendar,
  Building2, Trash2, Pencil, ShieldAlert, Network, Send, CheckCircle2, Ban, Eye,
  NotebookPen, Sparkles, Clock, ListChecks, ChevronDown, ChevronRight, GitMerge,
  Webhook, Copy, KeyRound, AlertTriangle, Rows3, ArrowUp, ArrowDown, ChevronsUpDown,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../ui/dropdown-menu";
import { STATUS_TONE } from "./prospects/prospectConstants";
import AddToNurture, { NurtureWarmBadge } from "./AddToNurture";
import LinkedActions, { useLinkedActionCounts, LinkedActionBadge } from "./LinkedActions";
import HubSpotHistory from "./HubSpotHistory";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { ContactsEditor } from "./ContactsEditor";
import ProspectFormDialog from "./prospects/ProspectFormDialog";
import ProspectSheet from "./prospects/ProspectSheet";
import ConvertToClientDialog from "./prospects/ConvertToClientDialog";
import ProspectList from "./prospects/ProspectList";
import {
  today, STATUS_META, STATUS_ORDER, SignupBadge, StatusBadge, emptyProspect,
  ACTIVITY_TYPES, CALL_OUTCOME_LABELS, CALL_OUTCOME_ORDER, NEXT_ACTION_LABELS,
} from "./prospects/prospectConstants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SalesProspects() {
  const navigate = useNavigate();
  const PAGE_SIZE = 25;
  const [prospects, setProspects] = useState([]);
  const actionCounts = useLinkedActionCounts("prospect");
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [stats, setStats] = useState({ counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("last_activity");
  const [categories, setCategories] = useState([]);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [showExistingClients, setShowExistingClients] = useState(false);
  const [reps, setReps] = useState([]);
  const [repFilter, setRepFilter] = useState("all");

  // Bulk selection + actions
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRep, setBulkRep] = useState("none");
  const [bulkStatus, setBulkStatus] = useState("none");
  const [applyingBulk, setApplyingBulk] = useState(false);
  // Manual merge (reuses the existing `merging` flag declared with dedupe state)
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeKeepId, setMergeKeepId] = useState("");

  // Rep workload
  const [workload, setWorkload] = useState({ reps: [], unassigned: 0 });

  const skipRef = useRef(0);
  const busyRef = useRef(false);
  const hasMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  // Add / edit
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // prospect id or null (=add)
  const [form, setForm] = useState(emptyProspect);
  const [saving, setSaving] = useState(false);

  // Detail
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState("details");
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem("equipr_sales_density") || "comfortable"; } catch { return "comfortable"; }
  });
  const cycleDensity = () => {
    const order = ["comfortable", "cozy", "compact"];
    const next = order[(order.indexOf(density) + 1) % order.length];
    setDensity(next);
    try { localStorage.setItem("equipr_sales_density", next); } catch {}
  };
  const [sortKey, setSortKey] = useState(null);   // client-side sort over loaded rows; null = server order
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (k) => {
    if (sortKey === k) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); }
    else { setSortKey(k); setSortDir("asc"); }
  };
  const DENSITY_PY = { compact: "py-2", cozy: "py-3", comfortable: "py-4" }[density] || "py-4";
  const DENSITY_LABEL = { compact: "Compact", cozy: "Cozy", comfortable: "Comfortable" }[density] || "Comfortable";
  const SortHead = ({ label, k, right }) => (
    <button type="button" onClick={() => toggleSort(k)}
      className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold hover:text-[#1A1A1A] transition-colors ${sortKey === k ? "text-[#1A1A1A]" : "text-[#8A857A]"} ${right ? "flex-row-reverse" : ""}`}
      data-testid={`sales-sort-${k}`}>
      {label}
      {sortKey === k ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ChevronsUpDown className="w-3 h-3 opacity-40" />}
    </button>
  );
  const [touch, setTouch] = useState({ type: "email", activity_date: today(), rep: "", outcome: "", notes: "", advance_status: "none", call_outcome: "none", callback_date: "" });
  const [loggingTouch, setLoggingTouch] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);

  // Duplicate cleanup
  const [dupOpen, setDupOpen] = useState(false);
  const [dupPreview, setDupPreview] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  // HubSpot import
  const [hsOpen, setHsOpen] = useState(false);
  const [hsRunning, setHsRunning] = useState(false);
  const [importingActivities, setImportingActivities] = useState(false);

  const importHubspotActivities = async () => {
    setImportingActivities(true);
    try {
      await axios.post(`${API}/sales/hubspot/import-activities`, {});
      toast.success("HubSpot activity import started — it runs in the background. Reopen a record's Activity tab in a few minutes.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start activity import");
    } finally {
      setImportingActivities(false);
    }
  };

  // Product sign-up webhook (EQUIPR Core & Guardian)
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupInfo, setSignupInfo] = useState(null);
  const [signupBusy, setSignupBusy] = useState(false);

  // Website lead-intake webhook (Contact Us / Demo Request push)
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadInfo, setLeadInfo] = useState(null);
  const [leadBusy, setLeadBusy] = useState(false);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [signupFilter, setSignupFilter] = useState("all");  // trial | trial_core | trial_guardian | active | churned

  const openLeadInfo = async () => {
    setLeadOpen(true);
    setLeadInfo(null);
    try {
      const r = await axios.get(`${API}/sales/leads/inbound-info`);
      setLeadInfo(r.data);
    } catch { toast.error("Failed to load lead webhook info"); }
  };

  const regenLeadSecret = async () => {
    setLeadBusy(true);
    try {
      const r = await axios.post(`${API}/sales/leads/inbound-secret/regenerate`, {});
      setLeadInfo(info => ({ ...(info || {}), secret: r.data.secret, configured: true }));
      toast.success("New secret generated — share with the website team");
    } catch { toast.error("Could not regenerate secret"); }
    finally { setLeadBusy(false); }
  };

  const openSignupInfo = async () => {
    setSignupOpen(true);
    setSignupInfo(null);
    try {
      const r = await axios.get(`${API}/sales/signups/webhook-info`);
      setSignupInfo(r.data);
    } catch { toast.error("Failed to load webhook info"); }
  };

  const regenSignupSecret = async () => {
    setSignupBusy(true);
    try {
      const r = await axios.post(`${API}/sales/signups/webhook-secret/regenerate`, {});
      setSignupInfo(info => ({ ...(info || {}), secret: r.data.secret, configured: true }));
      toast.success("New secret generated — update Core & Guardian");
    } catch { toast.error("Could not regenerate secret"); }
    finally { setSignupBusy(false); }
  };

  const copyText = (t, label) => { if (t) { navigator.clipboard.writeText(t); toast.success(`${label} copied`); } };

  // Demo / meeting notes
  const [meetings, setMeetings] = useState([]);
  const [expandedMeeting, setExpandedMeeting] = useState(null);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ raw_notes: "", meeting_date: "", advance_status: "demo_completed" });
  const [savingMeeting, setSavingMeeting] = useState(false);

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(null); // prospect obj or null
  const [deleting, setDeleting] = useState(false);

  // Email compose
  const [me, setMe] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [salesSettings, setSalesSettings] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeContacts, setComposeContacts] = useState([]);
  const [compose, setCompose] = useState({
    template_id: "", subject: "", body_html: "", from_name: "", from_email: "", to_email: "",
  });
  const [previewHtml, setPreviewHtml] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const [sRes, wRes] = await Promise.all([
        axios.get(`${API}/sales/prospects/stats`),
        axios.get(`${API}/sales/prospects/workload`),
      ]);
      setStats(sRes.data || { counts: {}, total: 0 });
      setWorkload(wRes.data || { reps: [], unassigned: 0 });
    } catch (e) { /* non-fatal */ }
  }, []);

  const fetchProspects = useCallback(async (reset) => {
    if (busyRef.current) return;
    busyRef.current = true;
    if (reset) { setLoading(true); skipRef.current = 0; setSelectedIds(new Set()); }
    else { setLoadingMore(true); }
    try {
      const params = {
        include_existing_clients: showExistingClients,
        limit: PAGE_SIZE,
        skip: reset ? 0 : skipRef.current,
      };
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (sourceFilter !== "all") params.source_type = sourceFilter;
      if (repFilter !== "all") params.assigned_rep = repFilter;
      if (sortBy) params.sort = sortBy;
      if (reviewOnly) params.needs_review = true;
      if (signupFilter === "ending") {
        params.trial_ending_within = 7;
      } else if (signupFilter !== "all") {
        const [life, prod] = signupFilter.split("_");
        params.signup_lifecycle = life;
        if (prod) params.signup_product = prod;
      }
      if (debouncedQuery.trim()) params.search = debouncedQuery.trim();
      const res = await axios.get(`${API}/sales/prospects`, { params });
      const batch = res.data.prospects || [];
      setProspects((prev) => (reset ? batch : [...prev, ...batch]));
      setTotal(res.data.total || 0);
      setHasMore(!!res.data.has_more);
      hasMoreRef.current = !!res.data.has_more;
      skipRef.current = (reset ? 0 : skipRef.current) + batch.length;
    } catch (e) {
      toast.error("Failed to load prospects");
    } finally {
      setLoading(false); setLoadingMore(false); busyRef.current = false;
    }
  }, [showExistingClients, statusFilter, categoryFilter, sourceFilter, repFilter, sortBy, reviewOnly, signupFilter, debouncedQuery]);

  const refresh = useCallback(() => { fetchProspects(true); fetchStats(); }, [fetchProspects, fetchStats]);

  const syncDemoRequests = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sales/sync-demo-requests`);
      toast.success(`Imported ${res.data.imported || 0} new demo request(s)`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demo sync not configured");
    } finally {
      setSyncing(false);
    }
  };

  // Debounce the free-text search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  // (Re)load page 0 whenever filters change
  useEffect(() => { fetchProspects(true); fetchStats(); }, [fetchProspects, fetchStats]);

  // Infinite scroll — load the next page when the sentinel enters view
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMoreRef.current && !busyRef.current) {
        fetchProspects(false);
      }
    }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchProspects]);

  useEffect(() => {
    axios.get(`${API}/agents/mapping`)
      .then(r => setReps((r.data.agents || []).filter(a => a.id !== "emergency")))
      .catch(() => {});
    axios.get(`${API}/auth/me`).then(r => setMe(r.data)).catch(() => {});
    axios.get(`${API}/sales/email-templates`).then(r => setTemplates(r.data.templates || [])).catch(() => {});
    axios.get(`${API}/sales/settings`).then(r => setSalesSettings(r.data.settings)).catch(() => {});
    axios.get(`${API}/sales/prospects/filters`)
      .then(r => { setCategories(r.data.categories || []); setSourceTypes(r.data.source_types || []); })
      .catch(() => {});
  }, []);

  const openAdd = () => { setEditing(null); setForm(emptyProspect); setFormOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      company_name: p.company_name || "", category: p.category || "", priority_tier: p.priority_tier || "",
      client_network_count: String(p.client_network_count ?? 0), contact_name: p.contact_name || "",
      contact_email: p.contact_email || "", contact_phone: p.contact_phone || "", suburb: p.suburb || "", country: p.country || "Australia", currency: (p.currency || "AUD").toUpperCase(),
      source: p.source || "", assigned_rep: p.assigned_rep || "", current_sequence_step: p.current_sequence_step || "",
      next_action_date: p.next_action_date || "", status: p.status || "not_contacted", notes: p.notes || "",
    });
    setFormOpen(true);
  };

  const saveProspect = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_network_count: parseInt(form.client_network_count, 10) || 0,
        next_action_date: form.next_action_date || null,
      };
      if (editing) {
        await axios.put(`${API}/sales/prospects/${editing}`, payload);
        toast.success("Prospect updated");
      } else {
        await axios.post(`${API}/sales/prospects`, payload);
        toast.success("Prospect added");
      }
      setFormOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save prospect");
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (p, initialTab = "details") => {
    setDetailTab(initialTab);
    try {
      const [res, mRes] = await Promise.all([
        axios.get(`${API}/sales/prospects/${p.id}`),
        axios.get(`${API}/sales/prospects/${p.id}/meetings`),
      ]);
      setSelected(res.data.prospect);
      setMeetings(mRes.data.meetings || []);
      setExpandedMeeting(null);
      setTouch({ type: "email", activity_date: today(), rep: "", outcome: "", notes: "", advance_status: "none", call_outcome: "none", callback_date: "" });
    } catch (e) {
      toast.error("Failed to load prospect");
    }
  };

  const saveMeeting = async () => {
    if (!selected) return;
    if (!meetingForm.raw_notes.trim()) { toast.error("Paste the meeting notes first"); return; }
    setSavingMeeting(true);
    try {
      const res = await axios.post(`${API}/sales/prospects/${selected.id}/meetings`, {
        raw_notes: meetingForm.raw_notes,
        meeting_date: meetingForm.meeting_date || null,
        advance_status: meetingForm.advance_status || null,
      });
      toast.success(res.data?.followup_created ? "Demo notes saved · follow-up created" : "Demo notes saved");
      setMeetingOpen(false);
      setMeetingForm({ raw_notes: "", meeting_date: "", advance_status: "demo_completed" });
      await openDetail(selected);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the notes");
    } finally {
      setSavingMeeting(false);
    }
  };

  const deleteMeeting = async (meetingId) => {
    if (!selected) return;
    try {
      await axios.delete(`${API}/sales/prospects/${selected.id}/meetings/${meetingId}`);
      toast.success("Meeting removed");
      setMeetings((prev) => prev.filter((m) => m.id !== meetingId));
    } catch (e) {
      toast.error("Failed to remove meeting");
    }
  };

  const openDupPreview = async () => {
    setDupOpen(true);
    setDupLoading(true);
    setDupPreview(null);
    try {
      const res = await axios.get(`${API}/sales/prospects/duplicates`);
      setDupPreview(res.data);
    } catch (e) {
      toast.error("Failed to scan for duplicates");
      setDupOpen(false);
    } finally {
      setDupLoading(false);
    }
  };

  const runHubspotImport = async () => {
    setHsRunning(true);
    try {
      const res = await axios.post(`${API}/sales/hubspot/import`, {}, { timeout: 240000 });
      toast.success(`HubSpot import: ${res.data.created} added, ${res.data.updated} updated, ${res.data.skipped_already_client} already clients`);
      setHsOpen(false);
      refresh();
    } catch (e) {
      if (e.code === "ECONNABORTED" || !e.response) {
        toast.info("Import is running in the background (large dataset). Refresh the list in a couple of minutes.");
        setHsOpen(false);
      } else {
        toast.error(e?.response?.data?.detail || "HubSpot import failed");
      }
    } finally {
      setHsRunning(false);
    }
  };

  const runMerge = async () => {
    setMerging(true);
    try {
      const res = await axios.post(`${API}/sales/prospects/merge-duplicates`);
      toast.success(`Merged ${res.data.groups_merged} companies · removed ${res.data.rows_removed} duplicate rows`);
      setDupOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  // Records currently selected (from the visible list)
  const selectedProspects = prospects.filter((p) => selectedIds.has(p.id));

  const openMergeDialog = () => {
    if (selectedIds.size < 2) { toast.error("Select at least two prospects to merge"); return; }
    // Default the survivor to the record furthest along the pipeline (most valuable to keep).
    const stageOrder = ["not_contacted", "demo_requested", "emailed", "called", "responded", "meeting_booked", "demo_completed", "quote_sent", "trialing", "converted"];
    const ranked = [...selectedProspects].sort((a, b) => stageOrder.indexOf(b.status) - stageOrder.indexOf(a.status));
    setMergeKeepId(ranked[0]?.id || selectedProspects[0]?.id || "");
    setMergeOpen(true);
  };

  const runManualMerge = async () => {
    const ids = Array.from(selectedIds);
    if (!mergeKeepId || ids.length < 2) return;
    setMerging(true);
    try {
      const res = await axios.post(`${API}/sales/prospects/merge`, { ids, keep_id: mergeKeepId });
      toast.success(`Merged ${res.data.removed + 1} records into one · removed ${res.data.removed}`);
      setMergeOpen(false);
      setSelectedIds(new Set());
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };


  const logTouch = async () => {
    if (!selected) return;
    setLoggingTouch(true);
    try {
      const res = await axios.post(`${API}/sales/activities`, {
        prospect_id: selected.id,
        type: touch.type,
        activity_date: touch.activity_date,
        rep: touch.rep || null,
        outcome: touch.outcome || null,
        notes: touch.notes || "",
        advance_status: touch.advance_status !== "none" ? touch.advance_status : null,
        call_outcome: touch.type === "call" && touch.call_outcome !== "none" ? touch.call_outcome : null,
        callback_date: touch.call_outcome === "callback_requested" && touch.callback_date ? touch.callback_date : null,
      });
      toast.success(res.data?.next_action_note || "Touch logged");
      await openDetail(selected);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log touch");
    } finally {
      setLoggingTouch(false);
    }
  };

  const setStatus = async (p, status) => {
    try {
      await axios.put(`${API}/sales/prospects/${p.id}`, { status });
      toast.success(status === "converted" ? "Converted → added to Clients" : `Marked ${STATUS_META[status].label}`);
      if (selected?.id === p.id) await openDetail(p);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  const [convertProspect, setConvertProspect] = useState(null);
  const openConvert = (p) => setConvertProspect(p);
  const onConverted = async () => {
    if (selected) await openDetail(selected);
    refresh();
  };

  const removeProspect = async (id) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/sales/prospects/${id}`);
      toast.success("Prospect deleted");
      setConfirmDelete(null);
      if (selected?.id === id) setSelected(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete prospect");
    } finally {
      setDeleting(false);
    }
  };

  const openCompose = async (p) => {
    const enquiry = ["demo_requested", "responded", "couldnt_reach", "custom"].includes(p.status) ? p.status : "responded";
    const preset = templates.find(t => t.enquiry_type === enquiry && t.active) || templates.find(t => t.active) || null;
    const fromName = me?.name || salesSettings?.default_from_name || "";
    const fromEmail = me?.email || salesSettings?.default_from_email || "";
    // Pull the prospect's contacts so the rep can pick who to send to.
    let contacts = [];
    try {
      const r = await axios.get(`${API}/sales/prospects/${p.id}/contacts`);
      contacts = (r.data.contacts || []).filter(c => (c.email || "").trim());
    } catch (e) { /* fall back to legacy contact_email */ }
    setComposeContacts(contacts);
    const defaultTo = (contacts.find(c => c.email === p.contact_email)?.email) || contacts[0]?.email || p.contact_email || "";
    setCompose({
      template_id: preset?.id || "",
      subject: preset?.subject || "",
      body_html: preset?.body_html || "",
      from_name: fromName,
      from_email: fromEmail,
      to_email: defaultTo,
    });
    setPreviewHtml("");
    setComposeOpen(true);
  };

  const contactLabel = (c) => {
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
    const role = c.role ? ` · ${c.role}` : "";
    return `${name || c.email}${role} — ${c.email}`;
  };

  const onSelectTemplate = (tid) => {
    const t = templates.find(x => x.id === tid);
    setCompose(c => ({ ...c, template_id: tid, subject: t?.subject ?? c.subject, body_html: t?.body_html ?? c.body_html }));
    setPreviewHtml("");
  };

  const previewEmail = async () => {
    if (!selected) return;
    try {
      const res = await axios.post(`${API}/sales/prospects/${selected.id}/email/preview`, {
        subject: compose.subject, body_html: compose.body_html, from_name: compose.from_name,
      });
      setPreviewHtml(res.data.body_html || "");
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const markFollowUpDone = async (id) => {
    try {
      await axios.post(`${API}/sales/prospects/${id}/follow-up/done`);
      toast.success("Follow-up cleared");
      if (selected?.id === id) await openDetail(selected);
      refresh();
    } catch (e) {
      toast.error("Failed to update follow-up");
    }
  };

  const sendEmail = async () => {
    if (!selected) return;
    if (!compose.to_email) { toast.error("Prospect has no contact email"); return; }
    if (!compose.subject.trim() || !compose.body_html.trim()) { toast.error("Subject and body are required"); return; }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/sales/prospects/${selected.id}/email/send`, {
        template_id: compose.template_id || null,
        subject: compose.subject,
        body_html: compose.body_html,
        from_name: compose.from_name || null,
        from_email: compose.from_email || null,
        to_email: compose.to_email || null,
      });
      toast.success(`Email sent to ${compose.to_email}`);
      setComposeOpen(false);
      await openDetail(selected);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };


  const importCsv = (file) => {
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const res = await axios.post(`${API}/sales/prospects/import`, { csv: ev.target.result });
        const d = res.data;
        toast.success(`Imported ${d.created} · ${d.skipped_duplicate} duplicate(s) skipped · ${d.suppressed_existing_client} existing client(s) skipped${d.errors ? ` · ${d.errors} error(s)` : ""}`);
        refresh();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Import failed — check the CSV format");
      } finally { setImporting(false); }
    };
    reader.onerror = () => { toast.error("Could not read file"); setImporting(false); };
    reader.readAsText(file);
  };


  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = prospects.length > 0 && prospects.every((p) => selectedIds.has(p.id));
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (prospects.every((p) => next.has(p.id))) {
        prospects.forEach((p) => next.delete(p.id));
      } else {
        prospects.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const applyBulk = async () => {
    if (selectedIds.size === 0) return;
    if (bulkRep === "none" && bulkStatus === "none") { toast.error("Pick a rep or a status to apply"); return; }
    setApplyingBulk(true);
    try {
      const body = { ids: Array.from(selectedIds) };
      if (bulkRep === "unassigned") body.clear_rep = true;
      else if (bulkRep !== "none") body.assigned_rep = bulkRep;
      if (bulkStatus !== "none") body.status = bulkStatus;
      const res = await axios.post(`${API}/sales/prospects/bulk-update`, body);
      toast.success(`Updated ${res.data.updated} prospect${res.data.updated === 1 ? "" : "s"}`);
      setBulkRep("none"); setBulkStatus("none");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setApplyingBulk(false);
    }
  };


  const filtersActive = statusFilter !== "all" || categoryFilter !== "all" || sourceFilter !== "all" || repFilter !== "all" || !!debouncedQuery.trim() || reviewOnly || showExistingClients || signupFilter !== "all";
  const filtered = (() => {
    if (!sortKey) return prospects;
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (p) => {
      if (sortKey === "status") return STATUS_ORDER.indexOf(p.status);
      if (sortKey === "next_action_date") return p.next_action_date ? new Date(p.next_action_date).getTime() : (dir === 1 ? Infinity : -Infinity);
      const v = p[sortKey];
      return typeof v === "string" ? v.toLowerCase() : (v ?? (dir === 1 ? Infinity : -Infinity));
    };
    return [...prospects].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  })();

  return (
    <div className="space-y-6" data-testid="sales-prospects-view">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-[27px] leading-tight font-extrabold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Prospects</h2>
          <p className="text-[13px] text-[#59554D] mt-0.5">Sales pipeline. <span className="mono">{stats.total}</span> records, <span className="mono">{stats.counts?.in_conversation ?? filtered.filter((p) => p.status && !["not_contacted", "disqualified", "converted"].includes(p.status)).length}</span> in active conversation.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" id="sales-csv-input" accept=".csv,text/csv" className="hidden" data-testid="sales-import-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) importCsv(f); e.target.value = ""; }} disabled={importing} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-9 border-[#C9C3B6] bg-white text-[#1A1A1A] hover:bg-[#F4F2ED] text-[12px] font-bold uppercase tracking-[0.04em]" data-testid="sales-import-sync">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing || importing || importingActivities ? "animate-spin" : ""}`} /> Import &amp; sync <ChevronDown className="w-3.5 h-3.5 ml-1.5 text-[#8A857A]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[248px] bg-[#FBFAF7] border-[#DAD5CA] rounded-xl p-1.5" style={{ boxShadow: "var(--shadow-pop)" }}>
              <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={syncDemoRequests} data-testid="sales-menu-sync-demo"><RefreshCw className="w-4 h-4 text-[#6B6660]" /> Sync demo requests</DropdownMenuItem>
              <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => setHsOpen(true)} data-testid="sales-menu-hubspot"><RefreshCw className="w-4 h-4 text-[#6B6660]" /> Import from HubSpot</DropdownMenuItem>
              <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={importHubspotActivities} data-testid="sales-menu-hubspot-activities"><RefreshCw className="w-4 h-4 text-[#6B6660]" /> Import HubSpot activities</DropdownMenuItem>
              <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => document.getElementById("sales-csv-input")?.click()} data-testid="sales-menu-csv"><Plus className="w-4 h-4 text-[#6B6660]" /> Import from CSV</DropdownMenuItem>
              <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={openDupPreview} data-testid="sales-menu-dedupe"><GitMerge className="w-4 h-4 text-[#6B6660]" /> Clean up duplicates</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#E6E2D9]" />
              <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={openSignupInfo} data-testid="sales-menu-signup"><Webhook className="w-4 h-4 text-[#6B6660]" /> Sign-up webhook</DropdownMenuItem>
              <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={openLeadInfo} data-testid="sales-menu-lead"><Webhook className="w-4 h-4 text-[#6B6660]" /> Website lead intake</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openAdd} className="h-9 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] text-[12px] font-bold uppercase tracking-[0.04em]" data-testid="sales-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add prospect
          </Button>
        </div>
      </div>

      {/* Pipeline — scale-break bar + clickable legend */}
      {(() => {
        const SEG = { not_contacted: "#C9C3B6", emailed: "#B0AA9E", called: "#9E988C", responded: "#3B82F6", demo_requested: "#E0B800", meeting_booked: "#0E6B3F", demo_completed: "#3B82F6", quote_sent: "#D97706", trialing: "#E0B800", converted: "#0E6B3F", disqualified: "#C9C3B6" };
        const rest = STATUS_ORDER.filter((s) => s !== "not_contacted");
        const restTotal = rest.reduce((a, s) => a + (stats.counts?.[s] || 0), 0) || 1;
        return (
          <div className="rounded-xl bg-white border border-[#DAD5CA] px-4 py-3.5" style={{ boxShadow: "var(--shadow-card)" }} data-testid="sales-pipeline">
            <div className="flex items-baseline gap-2 mb-2.5">
              <span className="text-[13px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>Pipeline</span>
              <span className="text-[11.5px] text-[#8A857A]">Click a stage to filter the register.</span>
            </div>
            <div className="flex items-center gap-1.5 mb-3" style={{ height: 26 }}>
              <div role="button" onClick={() => setStatusFilter(statusFilter === "not_contacted" ? "all" : "not_contacted")}
                className="rounded-[5px] cursor-pointer" title={`Not contacted · ${stats.counts?.not_contacted ?? 0}`}
                style={{ flex: "0 0 40%", height: "100%", background: SEG.not_contacted, opacity: statusFilter === "not_contacted" ? 1 : 0.85 }} data-testid="sales-bar-not_contacted" />
              <span className="shrink-0 text-[#B5AFA3] select-none" style={{ transform: "skewX(-12deg)", fontWeight: 700 }} title="scale break">/</span>
              <div className="flex items-center gap-[3px]" style={{ flex: "1 1 auto", height: "100%" }}>
                {rest.map((s) => {
                  const c = stats.counts?.[s] || 0;
                  const w = Math.max(c > 0 ? (c / restTotal) * 100 : 0, c > 0 ? 3 : 0.8);
                  return <div key={s} role="button" onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
                    className="rounded-[5px] cursor-pointer" title={`${STATUS_META[s].label} · ${c}`}
                    style={{ flex: `1 1 ${w}%`, minWidth: c > 0 ? 10 : 3, height: "100%", background: SEG[s], opacity: c === 0 ? 0.28 : (statusFilter === s ? 1 : 0.9) }} data-testid={`sales-bar-${s}`} />;
                })}
              </div>
            </div>
            <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap" data-testid="sales-legend">
              {STATUS_ORDER.map((s) => {
                const c = stats.counts?.[s] || 0;
                const active = statusFilter === s;
                return (
                  <button key={s} onClick={() => setStatusFilter(active ? "all" : s)} data-testid={`sales-stat-${s}`}
                    className={`inline-flex items-center gap-1.5 rounded-full transition-colors ${active ? "bg-[#FFF8D6] border border-[#E8D48A] px-2 py-0.5" : "px-1 py-0.5"}`}
                    style={{ opacity: c === 0 && !active ? 0.5 : 1 }}>
                    <span className="rounded-full" style={{ width: 6, height: 6, background: SEG[s] }} />
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-[#59554D]">{STATUS_META[s].label}</span>
                    <span className="mono text-[11px] text-[#1A1A1A]">{c}</span>
                  </button>
                );
              })}
            </div>
            {(stats.signup?.trial || stats.signup?.active || stats.signup?.churned) ? (
              <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap mt-3 pt-3 border-t border-[#EDE9E0]"
                data-testid="sales-signups">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-[#8A857A]"
                  title="Core and Guardian sign-ups. Counts exclude prospects already matched to a client, like the pipeline above.">Sign-ups</span>
                {[
                  ["trial", "Trials", stats.signup?.trial || 0, "#E0B800"],
                  ["ending", "Ending this week", stats.signup?.trial_ending || 0, "#C23A00"],
                  ["trial_core", "Core trial", stats.signup?.trial_core || 0, "#3B82F6"],
                  ["trial_guardian", "Guardian trial", stats.signup?.trial_guardian || 0, "#0E6B3F"],
                  ["active", "Paying", stats.signup?.active || 0, "#0E6B3F"],
                  ["churned", "Churned", stats.signup?.churned || 0, "#C9C3B6"],
                ].map(([key, label, count, dot]) => {
                  const active = signupFilter === key;
                  return (
                    <button key={key} onClick={() => setSignupFilter(active ? "all" : key)}
                      data-testid={`sales-signup-${key}`}
                      title={key === "ending"
                        ? "Trials ending within 7 days (or just past). Each one has a chase to-do with its rep."
                        : `${label} — click to filter the register`}
                      className={`inline-flex items-center gap-1.5 rounded-full transition-colors ${active ? "bg-[#FFF8D6] border border-[#E8D48A] px-2 py-0.5" : "px-1 py-0.5"}`}
                      style={{ opacity: count === 0 && !active ? 0.5 : 1 }}>
                      <span className="rounded-full" style={{ width: 6, height: 6, background: dot }} />
                      <span className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-[#59554D]">{label}</span>
                      <span className="mono text-[11px] text-[#1A1A1A]">{count}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })()}

      {/* Register — one banded card (search, workload, ask all live inside) */}
      <ProspectList ctx={{
        loading, filtered, total, filtersActive, selectedIds, reps,
        bulkRep, setBulkRep, bulkStatus, setBulkStatus, applyingBulk, applyBulk, openMergeDialog, setSelectedIds,
        allVisibleSelected, toggleSelectAllVisible, toggleSelect,
        cycleDensity, fetchProspects, DENSITY_LABEL, DENSITY_PY, SortHead, sortKey, sortDir, actionCounts,
        openDetail: (p, tab) => navigate(`/prospects/${p.id}${tab === "activity" ? "?action=log-touch" : ""}`), openEdit, setConfirmDelete, openFollowUp: (p) => navigate(`/prospects/${p.id}?action=follow-up`), hasMore, loadingMore, sentinelRef,
        setCategoryFilter, setSourceFilter, setStatusFilter, setRepFilter, setQuery,
        query, categoryFilter, sourceFilter, sortBy, setSortBy, categories, sourceTypes,
        workload, repFilter, reviewOnly, setReviewOnly, stats, signupFilter, setSignupFilter,
      }} />

      {/* Add / Edit dialog */}
      <ProspectFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        reps={reps}
        onSave={saveProspect}
        saving={saving}
      />

      {/* Detail — tabbed side sheet (read-only view; Edit opens the form modal) */}
      <ProspectSheet ctx={{
        selected, setSelected, detailTab, setDetailTab, meetings, expandedMeeting, setExpandedMeeting,
        touch, setTouch, loggingTouch, reps, API,
        markFollowUpDone, openCompose, setStatus, openConvert, setMeetingForm, setMeetingOpen, deleteMeeting,
        logTouch, setConfirmDelete, openEdit,
      }} />

      <ConvertToClientDialog
        prospect={convertProspect}
        open={!!convertProspect}
        onOpenChange={(o) => { if (!o) setConvertProspect(null); }}
        staff={reps}
        onDone={onConverted}
      />

      {/* Compose email dialog */}
      <Dialog open={composeOpen} onOpenChange={(o) => { if (!o && !sendingEmail) setComposeOpen(false); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sales-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#8A6D00]" /> Send Email{selected ? ` — ${selected.company_name}` : ""}</DialogTitle>
            <p className="text-xs text-[#8A857A]">Tokens like {"{{first_name}}"} and {"{{company_name}}"} are auto-filled from this prospect.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#59554D]">Template</label>
              <Select value={compose.template_id || "none"} onValueChange={(v) => onSelectTemplate(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-email-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                  <SelectItem value="none">No template (write from scratch)</SelectItem>
                  {templates.filter(t => t.active).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">From name</label>
                <Input value={compose.from_name} onChange={(e) => setCompose({ ...compose, from_name: e.target.value })}
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-email-from-name" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">From email</label>
                <Input value={compose.from_email} onChange={(e) => setCompose({ ...compose, from_email: e.target.value })}
                  placeholder="you@equipr.software" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-email-from-email" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Recipient</label>
              {composeContacts.length > 0 ? (
                <Select
                  value={composeContacts.some(c => c.email === compose.to_email) ? compose.to_email : "__custom__"}
                  onValueChange={(v) => { if (v !== "__custom__") setCompose(c => ({ ...c, to_email: v })); else setCompose(c => ({ ...c, to_email: "" })); }}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-email-recipient">
                    <SelectValue placeholder="Choose a contact" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    {composeContacts.map((c) => (
                      <SelectItem key={c.id || c.email} value={c.email} data-testid={`sales-email-recipient-${c.id || c.email}`}>{contactLabel(c)}</SelectItem>
                    ))}
                    <SelectItem value="__custom__">Custom email…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-[#8A857A] mt-1">No saved contacts — type an email below.</p>
              )}
            </div>
            <div>
              <label className="text-xs text-[#59554D]">To</label>
              <Input value={compose.to_email} onChange={(e) => setCompose({ ...compose, to_email: e.target.value })}
                placeholder="name@company.com" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-email-to" />
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Subject</label>
              <Input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-email-subject" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs text-[#59554D]">Body (HTML)</label>
                <Button size="sm" variant="ghost" onClick={previewEmail} className="h-6 text-xs text-[#8A6D00] hover:bg-[#FFF8D6]" data-testid="sales-email-preview-btn">
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
              </div>
              <Textarea value={compose.body_html} onChange={(e) => setCompose({ ...compose, body_html: e.target.value })}
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] min-h-[160px] font-mono text-xs" data-testid="sales-email-body" />
            </div>
            {previewHtml && (
              <div className="rounded-lg border border-[#DAD5CA] bg-white p-4 text-black text-sm" data-testid="sales-email-preview">
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setComposeOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] font-medium" data-testid="sales-email-send">
              {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HubSpot import confirm */}
      <Dialog open={hsOpen} onOpenChange={(o) => { if (!o && !hsRunning) setHsOpen(false); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg" data-testid="sales-hubspot-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-[#8A6D00]" /> Import from HubSpot</DialogTitle>
            <p className="text-xs text-[#8A857A]">Pulls your HubSpot companies in at the company level (contacts rolled onto each company, so no per-person duplicates).</p>
          </DialogHeader>
          <ul className="text-sm text-[#59554D] space-y-2 list-disc pl-5">
            <li>Non-partner companies (Prospect / Reseller / Vendor / Other / blank) are added as <span className="text-[#1A1A1A]">Prospects</span>.</li>
            <li><span className="text-[#1A1A1A]">Partners</span> already appear in your Client Listing, so they're skipped here.</li>
            <li>Anything already a client, or previously imported, is skipped or updated — <span className="text-[#1A1A1A]">no double-ups</span>.</li>
            <li>Contacts (name / email / phone) are recorded against the company.</li>
          </ul>
          <p className="text-xs text-[#6B5600]">This scans ~2,400 companies and can take a couple of minutes; it keeps running even if this dialog closes.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHsOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={runHubspotImport} disabled={hsRunning} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] font-medium" data-testid="sales-hubspot-confirm">
              {hsRunning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {hsRunning ? "Importing…" : "Import now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product sign-up webhook (EQUIPR Core & Guardian) */}
      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl" data-testid="signup-webhook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Webhook className="w-4 h-4 text-[#8A6D00]" /> Product Sign-up Webhook</DialogTitle>
            <p className="text-xs text-[#8A857A]">Point EQUIPR Core &amp; Guardian at this endpoint. New sign-ups land in Prospects as <span className="text-[#6B5600]">Trialing</span>; a paid/converted event auto-moves them to a Customer Success client.</p>
          </DialogHeader>
          {!signupInfo ? (
            <div className="p-8 text-center text-[#8A857A]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-1">POST URL</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[12px] text-[#8A6D00] bg-[#FFFFFF] border border-[#DAD5CA] rounded px-2 py-1.5 truncate">{signupInfo.url}</code>
                  <Button size="sm" variant="outline" className="border-[#DAD5CA] bg-transparent text-[#59554D] shrink-0" onClick={() => copyText(signupInfo.url, "URL")} data-testid="signup-copy-url"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-1">Shared secret — header <code className="text-[#59554D]">X-Signup-Secret</code></div>
                {signupInfo.secret ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[12px] text-[#0E6B3F] bg-[#FFFFFF] border border-[#DAD5CA] rounded px-2 py-1.5 truncate" data-testid="signup-secret">{signupInfo.secret}</code>
                    <Button size="sm" variant="outline" className="border-[#DAD5CA] bg-transparent text-[#59554D] shrink-0" onClick={() => copyText(signupInfo.secret, "Secret")} data-testid="signup-copy-secret"><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                ) : (
                  <div className="text-sm text-[#6B5600] flex items-center gap-2"><KeyRound className="w-4 h-4" /> No secret yet — generate one to enable the webhook.</div>
                )}
                <Button size="sm" onClick={regenSignupSecret} disabled={signupBusy} className="mt-2 bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]" data-testid="signup-regen-secret">
                  {signupBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />} {signupInfo.secret ? "Regenerate secret" : "Generate secret"}
                </Button>
                {signupInfo.secret && <p className="text-[11px] text-[#8A857A] mt-1">Regenerating invalidates the old secret — update Core &amp; Guardian afterwards.</p>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-1">Sample JSON body</div>
                <pre className="text-[11px] text-[#59554D] bg-[#FFFFFF] border border-[#DAD5CA] rounded p-3 overflow-x-auto" data-testid="signup-sample">{JSON.stringify(signupInfo.sample_payload, null, 2)}</pre>
                <p className="text-[11px] text-[#8A857A] mt-1"><span className="text-[#59554D]">product</span>: "core" or "guardian" · <span className="text-[#59554D]">event</span>: "signup" (trial) or "converted"/<span className="text-[#59554D]">is_paid:true</span> (becomes a paying client) · <span className="text-[#59554D]">account_id</span> keeps repeat events on the same record.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSignupOpen(false)} className="text-[#59554D]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Website lead-intake webhook (Contact Us / Demo Request push) */}
      <Dialog open={leadOpen} onOpenChange={setLeadOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl" data-testid="lead-webhook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Webhook className="w-4 h-4 text-[#0E6B3F]" /> Website Lead Intake</DialogTitle>
            <p className="text-xs text-[#8A857A]">Give this URL + secret to your website team so every Contact Us / Demo Request is <span className="text-[#0E6B3F]">pushed here instantly</span>. Flexible field names are accepted, and any enquiry with missing details is captured and flagged <span className="text-[#6B5600]">Needs review</span> — never dropped.</p>
          </DialogHeader>
          {!leadInfo ? (
            <div className="p-8 text-center text-[#8A857A]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-1">POST URL</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[12px] text-[#0E6B3F] bg-[#FFFFFF] border border-[#DAD5CA] rounded px-2 py-1.5 truncate">{leadInfo.url}</code>
                  <Button size="sm" variant="outline" className="border-[#DAD5CA] bg-transparent text-[#59554D] shrink-0" onClick={() => copyText(leadInfo.url, "URL")} data-testid="lead-copy-url"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-1">Shared secret — header <code className="text-[#59554D]">X-Webhook-Secret</code></div>
                {leadInfo.secret ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[12px] text-[#0E6B3F] bg-[#FFFFFF] border border-[#DAD5CA] rounded px-2 py-1.5 truncate" data-testid="lead-secret">{leadInfo.secret}</code>
                    <Button size="sm" variant="outline" className="border-[#DAD5CA] bg-transparent text-[#59554D] shrink-0" onClick={() => copyText(leadInfo.secret, "Secret")} data-testid="lead-copy-secret"><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                ) : (
                  <div className="text-sm text-[#6B5600] flex items-center gap-2"><KeyRound className="w-4 h-4" /> No secret yet — generate one to enable the push endpoint.</div>
                )}
                <Button size="sm" onClick={regenLeadSecret} disabled={leadBusy} className="mt-2 bg-[#0E6B3F] hover:bg-[#E3F3E9] text-black" data-testid="lead-regen-secret">
                  {leadBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />} {leadInfo.secret ? "Regenerate secret" : "Generate secret"}
                </Button>
                {leadInfo.secret && <p className="text-[11px] text-[#8A857A] mt-1">Regenerating invalidates the old secret — re-share with the website team afterwards.</p>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-1">Sample JSON body</div>
                <pre className="text-[11px] text-[#59554D] bg-[#FFFFFF] border border-[#DAD5CA] rounded p-3 overflow-x-auto" data-testid="lead-sample">{JSON.stringify(leadInfo.sample_payload, null, 2)}</pre>
                <p className="text-[11px] text-[#8A857A] mt-1">Send a stable, unique <span className="text-[#59554D]">id</span> per submission so re-delivery never duplicates. Any form whose name contains "demo" lands in <span className="text-[#59554D]">Demo Requested</span>.</p>
              </div>
              {leadInfo.accepted_field_aliases && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-1">Accepted field names</div>
                  <div className="text-[11px] text-[#59554D] bg-[#FFFFFF] border border-[#DAD5CA] rounded p-3 space-y-0.5">
                    {Object.entries(leadInfo.accepted_field_aliases).map(([k, v]) => (
                      <div key={k}><span className="text-[#0E6B3F]">{k}</span>: {Array.isArray(v) ? v.join(", ") : String(v)}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLeadOpen(false)} className="text-[#59554D]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Duplicate cleanup preview + confirm */}
      {/* Manual merge (multi-select) */}
      <Dialog open={mergeOpen} onOpenChange={(o) => { if (!o && !merging) setMergeOpen(false); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg" data-testid="sales-merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitMerge className="w-4 h-4 text-[#8A6D00]" /> Merge {selectedProspects.length} prospects</DialogTitle>
            <p className="text-xs text-[#8A857A]">Pick the record to keep. Activities, meetings and nurture enrolments from the others move onto it, then the other records are deleted. This can't be undone.</p>
          </DialogHeader>
          <div className="space-y-2 py-1 max-h-[320px] overflow-y-auto">
            {selectedProspects.map((p) => (
              <label key={p.id}
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${mergeKeepId === p.id ? "border-[#8A6D00] bg-[#FFF8D6]" : "border-[#DAD5CA] bg-[#FFFFFF] hover:border-[#C9C3B6]"}`}
                data-testid={`sales-merge-option-${p.id}`}>
                <input type="radio" name="merge-keep" checked={mergeKeepId === p.id} onChange={() => setMergeKeepId(p.id)}
                  className="mt-1 accent-[#8A6D00]" data-testid={`sales-merge-radio-${p.id}`} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1A1A1A] truncate">{p.company_name || "(no name)"}</div>
                  <div className="text-[11px] text-[#8A857A]">
                    {(p.status && STATUS_META[p.status]?.label) || p.status || "—"}
                    {p.assigned_rep ? ` · ${p.assigned_rep}` : ""}
                    {p.contact_name ? ` · ${p.contact_name}` : ""}
                  </div>
                </div>
                {mergeKeepId === p.id && <span className="ml-auto text-[10px] uppercase tracking-wide text-[#8A6D00] font-semibold">Keep</span>}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)} disabled={merging}
              className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#DAD5CA] hover:text-[#1A1A1A]" data-testid="sales-merge-cancel">
              Cancel
            </Button>
            <Button onClick={runManualMerge} disabled={merging || !mergeKeepId}
              className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-medium" data-testid="sales-merge-confirm">
              {merging && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Merge into selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={dupOpen} onOpenChange={(o) => { if (!o && !merging) setDupOpen(false); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sales-dedupe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitMerge className="w-4 h-4 text-[#8A6D00]" /> Clean up duplicate companies</DialogTitle>
            <p className="text-xs text-[#8A857A]">Companies split across several rows (e.g. "Smithworx Earthmoving - Mitch Adams") will be collapsed into one entity named after the company. Extra rows are deleted — nothing happens until you confirm.</p>
          </DialogHeader>
          {dupLoading ? (
            <div className="p-10 text-center text-[#8A857A]"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Scanning…</div>
          ) : !dupPreview ? null : dupPreview.group_count === 0 ? (
            <div className="p-8 text-center text-sm text-[#59554D]" data-testid="sales-dedupe-none">No duplicate companies found — your list is clean.</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3 text-center">
                  <div className="text-2xl font-semibold text-[#1A1A1A]" data-testid="dedupe-groups">{dupPreview.group_count}</div>
                  <div className="text-xs text-[#8A857A]">companies</div>
                </div>
                <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3 text-center">
                  <div className="text-2xl font-semibold text-[#6B5600]">{dupPreview.rows_affected}</div>
                  <div className="text-xs text-[#8A857A]">rows now</div>
                </div>
                <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3 text-center">
                  <div className="text-2xl font-semibold text-[#0E6B3F]" data-testid="dedupe-removed">−{dupPreview.rows_removed}</div>
                  <div className="text-xs text-[#8A857A]">rows removed</div>
                </div>
              </div>
              <div className="max-h-[280px] overflow-y-auto space-y-2 pr-1">
                {dupPreview.groups.map((g, i) => (
                  <div key={i} className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] px-3 py-2" data-testid={`dedupe-group-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#1A1A1A] font-medium">{g.base}</span>
                      <Badge className="bg-[#FFF8D6] text-[#8A6D00] border-0">{g.count} → 1</Badge>
                    </div>
                    <div className="text-xs text-[#8A857A] mt-1 truncate">{g.names.join("  ·  ")}</div>
                  </div>
                ))}
                {dupPreview.group_count > dupPreview.groups.length && (
                  <div className="text-xs text-[#8A857A] text-center pt-1">+ {dupPreview.group_count - dupPreview.groups.length} more…</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDupOpen(false)} className="text-[#59554D]">Cancel</Button>
            {dupPreview && dupPreview.group_count > 0 && (
              <Button onClick={runMerge} disabled={merging} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] font-medium" data-testid="sales-dedupe-confirm">
                {merging ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <GitMerge className="w-4 h-4 mr-1" />}
                {merging ? "Merging…" : `Merge ${dupPreview.group_count} companies`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add demo / meeting notes dialog */}
      <Dialog open={meetingOpen} onOpenChange={(o) => { if (!o && !savingMeeting) setMeetingOpen(false); }}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sales-meeting-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#3B82F6]" /> Add demo notes{selected ? ` — ${selected.company_name}` : ""}
            </DialogTitle>
            <p className="text-xs text-[#8A857A]">Paste the raw Teams AI meeting notes. We'll auto-summarise them, log a meeting, move the prospect to <span className="text-[#3B82F6]">Demo Completed</span>, and turn your action items into a follow-up.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Meeting date</label>
                <Input type="date" value={meetingForm.meeting_date}
                  onChange={(e) => setMeetingForm({ ...meetingForm, meeting_date: e.target.value })}
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-meeting-date" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Set status to</label>
                <Select value={meetingForm.advance_status} onValueChange={(v) => setMeetingForm({ ...meetingForm, advance_status: v })}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-meeting-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <SelectItem value="none">Leave unchanged</SelectItem>
                    {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Meeting notes (paste from Teams)</label>
              <Textarea value={meetingForm.raw_notes} onChange={(e) => setMeetingForm({ ...meetingForm, raw_notes: e.target.value })}
                placeholder="Paste the full AI meeting notes here — purpose, takeaways, summary, action items…"
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] min-h-[240px] text-xs" data-testid="sales-meeting-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMeetingOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={saveMeeting} disabled={savingMeeting || !meetingForm.raw_notes.trim()}
              className="bg-[#3B82F6] hover:bg-[#EFF6FF] text-black font-medium" data-testid="sales-meeting-save">
              {savingMeeting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {savingMeeting ? "Summarising…" : "Summarise & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o && !deleting) setConfirmDelete(null); }}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete prospect?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#59554D]">
              This permanently removes <span className="text-[#1A1A1A] font-medium">{confirmDelete?.company_name}</span> and all its logged activities. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid="sales-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete && removeProspect(confirmDelete.id); }}
              disabled={deleting} className="bg-[#C23A00] hover:bg-[#FFE9E0] text-[#1A1A1A]" data-testid="sales-delete-confirm-btn">
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
